import { useAuth0 } from '@auth0/auth0-react';
import {
  useAssignResourcesHomeSite,
  useCreateResource,
  useDeleteResource,
  useResources,
  useSitesWithMembers,
  useUpdateResource,
} from '@my/directory-api/hooks';
import type {
  CreateResourceRequest,
  Resource,
  ResourceTypeValue,
  UpdateResourceRequest,
} from '@my/directory-api';
import { useServiceTags } from '@my/tms-api/hooks';
import type { ServiceTag } from '@my/tms-api';
import { ConfirmDialog, ErrorBanner, Loading, PageHeader, SelectInput, showToast } from '@my/ui';
import { ChevronDown, ChevronUp, ChevronsUpDown, Edit, Plus, Search } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { DirectoryHistoryButton } from '@/components/HistoryButtons';
import { ResourceEditor } from './ResourceEditor';
import { emptyDraft, toDraft, type ResourceDraft } from './lib/resourceDraft';

// ────────────────────────────────────────────────────────────────────────────
// Resources page — TMS-side counterpart to My.Tms.Old's
// `ResourcesSettingsPage`. The grid lists every fleet resource (driver,
// truck, rigid, trailer, other) with its compliance roll-up and lets the
// operator filter by type / compliance / yes-no, search by name/email/
// mobile, and sort by name/type/compliance.
//
// Wave 1 (this file): page skeleton — header, filters, search,
// read-only table. The inline editor + cert picker + attachment upload
// land in subsequent waves; for now the +Add and edit actions are
// stubbed with console.log placeholders.
//
// Org id resolution mirrors CentralConfigurationPage: pull from JWT
// claim until per-org switching lands in the shared account store.
// ────────────────────────────────────────────────────────────────────────────

const TYPE_ORDER: ResourceTypeValue[] = ['Driver', 'Truck', 'Rigid', 'Trailer', 'Other'];

/** Sentinel in the bulk depot picker meaning "unassign the depot". */
const CLEAR_DEPOT = '__clear__';

type SortKey = 'name' | 'type' | 'compliance';
type SortDir = 'asc' | 'desc';
type TypeFilter = ResourceTypeValue | 'all';
type ComplianceValue = 'Red' | 'Yellow' | 'Green';
type ComplianceFilter = ComplianceValue | 'all';
type CompliantFilter = 'all' | 'yes' | 'no';

// Indicator → dot bg colour. Order matters for sort severity.
const COMPLIANCE_DOT: Record<ComplianceValue, string> = {
  Red: 'bg-red-500',
  Yellow: 'bg-yellow-400',
  Green: 'bg-green-500',
};

// Lower = more urgent (red first). Drives `sort by compliance ascending`.
const COMPLIANCE_SEVERITY: Record<ComplianceValue, number> = {
  Red: 0,
  Yellow: 1,
  Green: 2,
};

export function ResourcesPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  // Multi-namespace: 'resources' is the primary lookup, 'common' is the
  // fallback for shared verbs / state words (`actions.add`, `states.*`).
  // The explicit `common:…` prefix below makes those references obvious
  // at the call site so future moves are easy to grep.
  const { t } = useTranslation(['resources', 'common']);

  const listQuery = useResources(orgId);
  // Service-tag library for the cert picker. Tags are configured org-wide
  // in Central Configuration and have an `applyToServiceTypes` enum slice
  // that gates where they show up; we filter to tags that include
  // 'Resources' (or the catch-all `null`) and only render those.
  const serviceTagsQuery = useServiceTags(orgId);
  const createMutation = useCreateResource();
  const updateMutation = useUpdateResource();
  const deleteMutation = useDeleteResource();

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  // Editor state. `null` = no editor open; `'new'` = drafting a fresh row;
  // a guid = editing the row with that id. Same triple-state pattern as
  // CargoDefinitionsSection / ChargesAndCostsSection.
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<ResourceDraft>(emptyDraft());
  const [pendingDelete, setPendingDelete] = useState<Resource | null>(null);

  // Pre-compute label dictionaries once per render. The typed-keys i18n
  // augmentation doesn't accept template-literal keys (every key has to
  // be a static string-literal it can prove exists in the JSON), so
  // dynamic lookups go through these maps instead of `t(`types.${x}`)`.
  const typeLabels: Record<ResourceTypeValue, string> = {
    Driver: t('types.driver'),
    Truck: t('types.truck'),
    Rigid: t('types.rigid'),
    Trailer: t('types.trailer'),
    Other: t('types.other'),
  };
  const complianceLabels: Record<ComplianceValue, string> = {
    Red: t('compliance.red'),
    Yellow: t('compliance.yellow'),
    Green: t('compliance.green'),
  };

  // ── UI state ─────────────────────────────────────────────────────────────

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<TypeFilter>('all');
  const [filterCompliance, setFilterCompliance] = useState<ComplianceFilter>('all');
  const [filterCompliant, setFilterCompliant] = useState<CompliantFilter>('all');

  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // ── Bulk depot assignment ────────────────────────────────────────────────

  const sitesQuery = useSitesWithMembers(orgId);
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  const siteNameById = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);

  const bulkAssign = useAssignResourcesHomeSite();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  /** Depot chosen in the bulk bar. '' = not chosen yet, '__clear__' = unassign. */
  const [bulkSiteId, setBulkSiteId] = useState('');

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  // ── Editor handlers ──────────────────────────────────────────────────────

  const startNew = () => {
    setEditingId('new');
    setDraft(emptyDraft());
  };

  const startEdit = (row: Resource) => {
    setEditingId(row.id);
    setDraft(toDraft(row));
  };

  const cancel = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const requestDelete = (row: Resource) => setPendingDelete(row);

  const handleSave = async () => {
    if (!orgId || editingId === null) return;
    if (!draft.type) return; // type-guard for the wire payload below

    const name = draft.name.trim();
    if (!name) return;

    const payload = {
      type: draft.type,
      name,
      displayName: draft.displayName.trim() || null,
      mobileNumber: draft.mobileNumber.trim() || null,
      email: draft.email.trim() || null,
      reference: draft.reference.trim() || null,
      organisationEquipmentTypeId: draft.organisationEquipmentTypeId || null,
      homeSiteId: draft.homeSiteId || null,
      defaultVehicleResourceId: draft.defaultVehicleResourceId || null,
      status: draft.status,
    };

    try {
      if (editingId === 'new') {
        const body: CreateResourceRequest = { organisationId: orgId, ...payload };
        await createMutation.mutateAsync(body);
        showToast({ type: 'success', message: 'Resource added.' });
      } else {
        const body: UpdateResourceRequest = { id: editingId, ...payload };
        await updateMutation.mutateAsync(body);
        showToast({ type: 'success', message: 'Resource updated.' });
      }
      cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save resource.',
      });
    }
  };

  const confirmDeletePending = async () => {
    const row = pendingDelete;
    if (!row || !orgId) return;
    try {
      await deleteMutation.mutateAsync({ id: row.id, organisationId: orgId });
      showToast({ type: 'success', message: 'Resource removed.' });
      if (editingId === row.id) cancel();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to delete resource.',
      });
    } finally {
      setPendingDelete(null);
    }
  };

  const hasActiveFilters =
    search.trim() !== '' ||
    filterType !== 'all' ||
    filterCompliance !== 'all' ||
    filterCompliant !== 'all';

  const clearFilters = () => {
    setSearch('');
    setFilterType('all');
    setFilterCompliance('all');
    setFilterCompliant('all');
  };

  // ── Derived data ─────────────────────────────────────────────────────────

  // Service-tag library for the cert picker — filtered to active tags
  // that opt-in to the Resources area. Tags with `applyToServiceTypes`
  // null/empty are catch-all (apply everywhere); otherwise the comma-
  // joined list is split and matched case-sensitively against the
  // canonical area name 'Resources'. Sorted alphabetically so the chip
  // order is stable across renders.
  const serviceTagsForResources: ServiceTag[] = useMemo(() => {
    return (serviceTagsQuery.data ?? [])
      .filter((tag) => {
        if (tag.status !== 'Active') return false;
        const apply = tag.applyToServiceTypes?.trim();
        if (!apply) return true; // null / empty = applies everywhere
        return apply
          .split(',')
          .map((s) => s.trim())
          .includes('Resources');
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [serviceTagsQuery.data]);

  const visibleResources = useMemo(() => {
    const all = listQuery.data ?? [];
    const q = search.trim().toLowerCase();
    const filtered = all.filter((r) => {
      if (q) {
        const haystack = [
          r.name,
          r.displayName ?? '',
          r.email ?? '',
          r.mobileNumber ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (filterType !== 'all' && r.type !== filterType) return false;
      if (filterCompliance !== 'all' && r.complianceIndicator !== filterCompliance) return false;
      if (filterCompliant === 'yes' && !r.isCompliant) return false;
      if (filterCompliant === 'no' && r.isCompliant) return false;
      return true;
    });
    return applySort(filtered, sortKey, sortDir);
  }, [listQuery.data, search, filterType, filterCompliance, filterCompliant, sortKey, sortDir]);

  const allVisibleSelected =
    visibleResources.length > 0 && visibleResources.every((r) => selectedIds.has(r.id));

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllVisible = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const r of visibleResources) next.delete(r.id);
      else for (const r of visibleResources) next.add(r.id);
      return next;
    });

  const clearSelection = () => {
    setSelectedIds(new Set());
    setBulkSiteId('');
  };

  /**
   * Assign the chosen depot to every selected resource in one request. The BE
   * rejects the whole batch if any id isn't in the org, so this is all-or-nothing
   * rather than a partial write.
   */
  const applyBulkDepot = async () => {
    if (!orgId || selectedIds.size === 0 || !bulkSiteId) return;
    try {
      const changed = await bulkAssign.mutateAsync({
        organisationId: orgId,
        resourceIds: [...selectedIds],
        homeSiteId: bulkSiteId === CLEAR_DEPOT ? null : bulkSiteId,
      });
      showToast({ type: 'success', message: t('bulk.done', { count: changed }) });
      clearSelection();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to assign depot.',
      });
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title={t('title')} description={t('description')}>
        {/* Whole-fleet rather than per-resource: the question is usually "what
            changed in the fleet lately", and a driver's certifications and
            roster blocks file under the driver either way. */}
        <DirectoryHistoryButton
          organisationId={orgId}
          aggregateType="Resource"
          wholeType
          subject={t('title')}
          variant="subtle"
        />
      </PageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {!orgId && (
          <ErrorBanner
            title="No organisation linked to this account"
            message="Your access token does not include a TMS organisation claim. Contact an administrator."
          />
        )}

        {orgId && listQuery.isPending && <Loading label={t('title')} />}

        {orgId && listQuery.isError && (
          <ErrorBanner title="Failed to load resources" message={listQuery.error.message} />
        )}

        {orgId && listQuery.isSuccess && (
          <>
            {/* ── Filters + search + add button ────────────────────────── */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Type chips */}
              {(['all', ...TYPE_ORDER] as TypeFilter[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFilterType(type)}
                  className={chipClass(filterType === type)}
                >
                  {type === 'all' ? t('filters.all') : typeLabels[type]}
                </button>
              ))}

              <span className="h-4 w-px bg-slate-300" aria-hidden="true" />

              {/* Compliance chips */}
              {(['all', 'Green', 'Yellow', 'Red'] as ComplianceFilter[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilterCompliance(c)}
                  className={`inline-flex items-center gap-1 ${chipClass(filterCompliance === c)}`}
                >
                  {c !== 'all' && (
                    <span
                      className={`h-1.5 w-1.5 rounded-full ${COMPLIANCE_DOT[c]}`}
                      aria-hidden="true"
                    />
                  )}
                  {c === 'all' ? t('filters.all') : complianceLabels[c]}
                </button>
              ))}

              <span className="h-4 w-px bg-slate-300" aria-hidden="true" />

              {/* Compliant yes/no chips */}
              {(['all', 'yes', 'no'] as CompliantFilter[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setFilterCompliant(v)}
                  className={chipClass(filterCompliant === v)}
                >
                  {v === 'all'
                    ? t('filters.all')
                    : v === 'yes'
                      ? t('filters.yes')
                      : t('filters.no')}
                </button>
              ))}

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[10px] text-slate-400 underline hover:text-slate-600"
                >
                  {t('filters.clear')}
                </button>
              )}

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder={t('search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* +Add — opens the inline editor in 'new' mode. Hidden
                  while another row is being edited so we never have two
                  drafts in flight. Verb comes from common:actions.add so
                  the same word reads identically across every screen. */}
              {editingId === null && (
                <button
                  type="button"
                  onClick={startNew}
                  disabled={isBusy}
                  className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('common:actions.add')}
                </button>
              )}
            </div>

            {/* ── Bulk depot assignment ─────────────────────────────────── */}
            {/* Appears once rows are ticked. One request assigns the depot to
                the whole selection — the point of the exercise is not editing
                several hundred imported trailers one at a time. */}
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap items-end gap-3 rounded-md border border-purple-200 bg-purple-50 p-3">
                <span className="pb-1.5 text-sm font-semibold text-slate-800">
                  {t('bulk.selected', { count: selectedIds.size })}
                </span>
                <div className="min-w-[12rem]">
                  <SelectInput
                    label={t('form.depot')}
                    name="bulk-depot"
                    value={bulkSiteId}
                    disabled={bulkAssign.isPending}
                    options={[
                      { value: '', label: t('form.noDepot') },
                      ...sites.map((s) => ({ value: s.id, label: s.name })),
                      { value: CLEAR_DEPOT, label: t('bulk.unassign') },
                    ]}
                    onChange={(e) => setBulkSiteId(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void applyBulkDepot()}
                  disabled={bulkAssign.isPending || !bulkSiteId}
                  className="rounded-md bg-purple-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {bulkAssign.isPending ? `${t('bulk.apply')}…` : t('bulk.apply')}
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={bulkAssign.isPending}
                  className="rounded-md px-2 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-200 disabled:opacity-50"
                >
                  {t('bulk.clear')}
                </button>
              </div>
            )}

            {/* ── Table ─────────────────────────────────────────────────── */}
            {/* `rounded-md` lives on the scroll container so the rounded
                corners are preserved; the inner div used to carry
                `overflow-hidden rounded-md` together but the
                `overflow-hidden` was clipping any table content that
                grew wider than `min-w-[768px]` (the editor's cert
                grid pushes wider than 768 on viewports between
                ~768–1024). With it clipped, the outer
                `overflow-x-auto` saw nothing past 768px and the user
                couldn't scroll to reach it on mobile. */}
            <div className="overflow-x-auto rounded-md">
              <div className="min-w-[768px]">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      <th className="w-8 px-3 py-2 text-left font-semibold">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          disabled={visibleResources.length === 0 || bulkAssign.isPending}
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all resources"
                          title="Select all (filtered)"
                          className="h-3.5 w-3.5 rounded border-slate-300"
                        />
                      </th>
                      <SortableTh
                        label={t('table.name')}
                        sortKey="name"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th>{t('table.displayName')}</Th>
                      <Th>{t('table.mobile')}</Th>
                      <Th>{t('table.email')}</Th>
                      <SortableTh
                        label={t('table.type')}
                        sortKey="type"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th>{t('table.depot')}</Th>
                      <SortableTh
                        label={t('table.compliance')}
                        sortKey="compliance"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                        align="center"
                      />
                      <Th align="center">{t('table.compliant')}</Th>
                      {/* Sticky-right Actions column — keeps the Edit
                          pencil reachable on mobile (table is wider
                          than the phone viewport). Solid slate-700
                          background so scrolling content can't bleed
                          through during horizontal scroll. */}
                      <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                        {t('table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-500 bg-white">
                    {/* Pinned new-row editor at the top — same affordance
                        the other settings sections use. */}
                    {editingId === 'new' && orgId && (
                      <tr>
                        <td colSpan={10} className="bg-slate-50 px-3 py-3">
                          <ResourceEditor
                            draft={draft}
                            isNew
                            organisationId={orgId}
                            serviceTags={serviceTagsForResources}
                            onChange={setDraft}
                            onSave={handleSave}
                            onCancel={cancel}
                            isBusy={isBusy}
                          />
                        </td>
                      </tr>
                    )}

                    {visibleResources.length === 0 && editingId !== 'new' ? (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-6 text-center text-xs italic text-slate-400"
                        >
                          {hasActiveFilters
                            ? t('table.emptyFiltered')
                            : t('table.empty', { add: t('common:actions.add') })}
                        </td>
                      </tr>
                    ) : (
                      visibleResources.map((r) => {
                        const isEditing = editingId === r.id;
                        return (
                          <Fragment key={r.id}>
                            <ResourceRow
                              resource={r}
                              typeLabel={typeLabels[r.type]}
                              complianceLabel={complianceLabels[r.complianceIndicator]}
                              yesLabel={t('filters.yes')}
                              noLabel={t('filters.no')}
                              depotName={
                                r.homeSiteId ? (siteNameById.get(r.homeSiteId) ?? null) : null
                              }
                              selected={selectedIds.has(r.id)}
                              onToggleSelect={() => toggleSelect(r.id)}
                              selectDisabled={bulkAssign.isPending}
                              isEditing={isEditing}
                              rowsLocked={editingId !== null && !isEditing}
                              onEdit={() => {
                                if (editingId === null && !isBusy) startEdit(r);
                              }}
                            />
                            {isEditing && orgId && (
                              <tr>
                                <td colSpan={10} className="bg-slate-50 px-3 py-3">
                                  <ResourceEditor
                                    draft={draft}
                                    isNew={false}
                                    organisationId={orgId}
                                    resource={r}
                                    serviceTags={serviceTagsForResources}
                                    onChange={setDraft}
                                    onSave={handleSave}
                                    onCancel={cancel}
                                    onDelete={() => requestDelete(r)}
                                    isBusy={isBusy}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('delete.title')}
        message={
          pendingDelete
            ? t('delete.message', { name: pendingDelete.displayName || pendingDelete.name })
            : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDeletePending}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </div>
  );
}

// ── Components ──────────────────────────────────────────────────────────────

function Th({
  children,
  align,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
}) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}>{children}</th>
  );
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | null;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'right' | 'center';
}) {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`group cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon
          className={`h-3 w-3 flex-shrink-0 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'
          }`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}

interface ResourceRowProps {
  resource: Resource;
  // Pre-resolved labels — avoids each row calling useTranslation again.
  typeLabel: string;
  complianceLabel: string;
  yesLabel: string;
  noLabel: string;
  /** Resolved home-depot site name, or null when unassigned. */
  depotName: string | null;
  /** Highlights the row being edited so the editor below is contextual. */
  isEditing: boolean;
  /** Another row is being edited — dim + disable click on this one. */
  rowsLocked: boolean;
  /** Bulk-selection state for the leading checkbox. */
  selected: boolean;
  onToggleSelect: () => void;
  selectDisabled: boolean;
  onEdit: () => void;
}

function ResourceRow({
  resource,
  typeLabel,
  complianceLabel,
  yesLabel,
  noLabel,
  depotName,
  isEditing,
  rowsLocked,
  selected,
  onToggleSelect,
  selectDisabled,
  onEdit,
}: ResourceRowProps) {
  const dotColour = COMPLIANCE_DOT[resource.complianceIndicator];

  // Visual states match CargoDefinitionsSection / ChargesAndCostsSection:
  //   - editing: highlighted background.
  //   - rows locked (someone else is editing): dimmed.
  //   - default: hover-able, double-click opens the editor.
  // `group` lets the sticky Actions cell pick up the row hover state
  // via `group-hover:bg-slate-50` — without it the sticky cell would
  // stay solid white through hover and look visually disconnected.
  const rowClass = isEditing
    ? 'group bg-slate-50'
    : rowsLocked
      ? 'group opacity-50'
      : 'group cursor-pointer hover:bg-slate-50';

  return (
    <tr
      className={rowClass}
      onDoubleClick={isEditing || rowsLocked ? undefined : onEdit}
    >
      {/* stopPropagation so ticking a row doesn't also trigger the row's
          double-click-to-edit affordance. */}
      <td className="w-8 px-3 py-1 align-top" onDoubleClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          disabled={selectDisabled}
          onChange={onToggleSelect}
          aria-label={`Select ${resource.displayName || resource.name}`}
          className="h-3.5 w-3.5 rounded border-slate-300"
        />
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-900">
        {resource.name}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {resource.displayName || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-500">
        {resource.mobileNumber || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="max-w-[160px] truncate px-3 py-1 align-top text-xs text-slate-500">
        {resource.email || <span className="italic text-slate-300">—</span>}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {typeLabel}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-600">
        {depotName ?? <span className="italic text-slate-300">—</span>}
      </td>
      <td className="px-3 py-1 align-top text-center">
        <span className="inline-flex items-center justify-center gap-1.5">
          <span
            className={`inline-block h-2 w-2 flex-shrink-0 rounded-full ${dotColour}`}
            aria-hidden="true"
          />
          <span className="text-[10px] font-medium text-slate-600">{complianceLabel}</span>
        </span>
      </td>
      <td className="px-3 py-1 align-top text-center">
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
            resource.isCompliant ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}
        >
          {resource.isCompliant ? yesLabel : noLabel}
        </span>
      </td>
      {/* Sticky-right pin — see matching `<th>` comment in the
          parent table. `bg-slate-50` when this row is the one being
          edited (matches the row's highlight); otherwise `bg-white`
          + `group-hover:bg-slate-50` to track the row's default
          hover state. Without an explicit background the scrolling
          content from sibling cells would bleed through. */}
      <td
        className={`sticky right-0 z-10 whitespace-nowrap px-3 py-1 align-top text-right ${
          isEditing ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'
        }`}
      >
        <button
          type="button"
          aria-label={`Edit ${resource.name}`}
          disabled={rowsLocked}
          onClick={(e) => {
            e.stopPropagation();
            if (!rowsLocked) onEdit();
          }}
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function applySort(list: Resource[], key: SortKey | null, dir: SortDir): Resource[] {
  if (!key) {
    // Default sort: type order then name. Matches the BE's ORDER BY.
    return [...list].sort((a, b) => {
      const ia = TYPE_ORDER.indexOf(a.type);
      const ib = TYPE_ORDER.indexOf(b.type);
      if (ia !== ib) return ia - ib;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
  }
  const mul = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (key) {
      case 'name':
        return mul * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      case 'type': {
        const ia = TYPE_ORDER.indexOf(a.type);
        const ib = TYPE_ORDER.indexOf(b.type);
        return mul * (ia - ib);
      }
      case 'compliance':
        return (
          mul *
          (COMPLIANCE_SEVERITY[a.complianceIndicator] -
            COMPLIANCE_SEVERITY[b.complianceIndicator])
        );
    }
  });
}

function chipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}
